import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { Combobox } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { Checkbox, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useWriteOffCandidates,
  useWriteOffs,
  writeOffTotals,
  type WriteOff,
} from '@/lib/accountant';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { useContacts } from '@/lib/contacts';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';
import { homeCurrency } from '@/lib/org-locale';
import { ltr } from '@/lib/arabic';
import { useLanguage, useT } from '@/lib/i18n';

type Tab = 'open' | 'history';

export const Route = createFileRoute('/accounting/write-off-invoices')({
  component: WriteOffInvoices,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    s['tab'] === 'history' ? { tab: 'history' } : {},
});

/** Overdue filter: days and the label key under accountingPages.writeOffPage. */
const OVERDUE = [
  [0, 'overdueAny'],
  [1, 'overdue'],
  [30, 'overdue30'],
  [60, 'overdue60'],
  [90, 'overdue90'],
  [180, 'overdue180'],
] as const;

/** Screen A9 (document 06): Write off invoices, QBO Accountant tools. */
function WriteOffInvoices() {
  const { tab = 'open' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const orgId = useOrgId();
  const t = useT();
  const tabClass = (on: boolean) =>
    cn(
      '-mb-px border-b-2 pb-2 text-sm',
      on
        ? 'border-primary font-medium'
        : 'border-transparent text-muted-foreground hover:text-foreground',
    );
  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;
  return (
    <div>
      <PageHeader
        title={t('accountingPages.writeOffPage.title')}
        description={t('accountingPages.writeOffPage.description')}
      />
      <div className="mb-4 flex items-center gap-6 border-b" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'open'}
          className={tabClass(tab === 'open')}
          onClick={() => void navigate({ search: {} })}
        >
          {t('accountingPages.writeOffPage.tabOpen')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'history'}
          className={tabClass(tab === 'history')}
          onClick={() => void navigate({ search: { tab: 'history' } })}
        >
          {t('accountingPages.writeOffPage.tabHistory')}
        </button>
      </div>
      {tab === 'history' ? <History /> : <Candidates />}
    </div>
  );
}

function Candidates() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const t = useT();
  const lang = useLanguage();
  const iso = (v: string) => (lang === 'ar' ? ltr(v) : v);
  const me = useMe();
  const isStaff = me.data?.isStaff ?? false;
  const accounts = useAccounts();
  const customers = useContacts({ kind: 'customer' });
  const [asOf, setAsOf] = useState(todayIso());
  const [minDays, setMinDays] = useState(1);
  const [contactId, setContactId] = useState<string | null>(null);
  const rows = useWriteOffCandidates({
    asOf,
    minDaysOverdue: minDays,
    ...(contactId ? { contactId } : {}),
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [relief, setRelief] = useState<Set<string>>(new Set());
  const [date, setDate] = useState(todayIso());
  const [accountId, setAccountId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [notified, setNotified] = useState(false);

  const expenseAccounts = useMemo(
    () => (accounts.data ?? []).filter((a) => a.isActive && a.class === 'expense' && !a.isControl),
    [accounts.data],
  );
  useEffect(() => {
    if (!accountId && expenseAccounts.length)
      setAccountId(
        (expenseAccounts.find((a) => a.detailType === 'bad_debts') ?? expenseAccounts[0])!.id,
      );
  }, [expenseAccounts, accountId]);
  useEffect(() => setDate(asOf), [asOf]);

  const list = rows.data ?? [];
  const totals = writeOffTotals(list, selected, relief);
  const toggle = (set: Set<string>, id: string) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  };
  const allOn = list.length > 0 && list.every((r) => selected.has(r.documentId));

  const write = useMutation({
    mutationFn: (reason: string) =>
      api<WriteOff[]>(`/orgs/${orgId}/write-offs`, {
        method: 'POST',
        body: JSON.stringify({
          date,
          badDebtAccountId: accountId,
          reason,
          // only the ticked invoices still shown: a filter change must not write off hidden ones
          invoices: list
            .filter((r) => selected.has(r.documentId))
            .map((r) => ({
              documentId: r.documentId,
              claimVatRelief: relief.has(r.documentId) && r.relief.eligible,
            })),
        }),
      }),
    onSuccess: async (done) => {
      await qc.invalidateQueries();
      setSelected(new Set());
      setRelief(new Set());
      setConfirming(false);
      toast(t('accountingPages.writeOffPage.toastDone', { count: done.length }));
    },
  });

  return (
    <>
      <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4">
        <Field label={t('accountingPages.writeOffPage.asOf')} className="w-40">
          {(id) => <DateInput id={id} value={asOf} onChange={setAsOf} />}
        </Field>
        <Field label={t('accountingPages.writeOffPage.show')} className="w-64">
          {(id) => (
            <NativeSelect
              id={id}
              value={minDays}
              onChange={(e) => setMinDays(Number(e.target.value))}
            >
              {OVERDUE.map(([v, l]) => (
                <option key={v} value={v}>
                  {t(`accountingPages.writeOffPage.${l}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('accountingPages.writeOffPage.customer')} className="w-72">
          {() => (
            <Combobox
              ariaLabel={t('accountingPages.writeOffPage.customer')}
              items={(customers.data ?? []).map((c) => ({ id: c.id, label: c.displayName }))}
              value={contactId}
              placeholder={t('accountingPages.writeOffPage.allCustomers')}
              onChange={setContactId}
            />
          )}
        </Field>
      </div>

      {rows.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : rows.isError ? (
        <EmptyState title={t('accountingPages.writeOffPage.loadError')} hint={rows.error.message} />
      ) : !list.length ? (
        <EmptyState
          title={t('accountingPages.writeOffPage.none')}
          hint={t('accountingPages.writeOffPage.noneHint')}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  {isStaff ? (
                    <input
                      type="checkbox"
                      aria-label={t('accountingPages.writeOffPage.selectAll')}
                      className="size-4 accent-primary"
                      checked={allOn}
                      onChange={() =>
                        setSelected(allOn ? new Set() : new Set(list.map((r) => r.documentId)))
                      }
                    />
                  ) : null}
                </TableHead>
                <TableHead>{t('accountingPages.writeOffPage.colInvoiceNo')}</TableHead>
                <TableHead>{t('accountingPages.writeOffPage.colCustomer')}</TableHead>
                <TableHead>{t('accountingPages.writeOffPage.colInvoiceDate')}</TableHead>
                <TableHead>{t('accountingPages.writeOffPage.colDueDate')}</TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.writeOffPage.colDaysOverdue')}
                </TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.writeOffPage.colTotal')}
                </TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.writeOffPage.colOpenBalance')}
                </TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.writeOffPage.colVatInBalance')}
                </TableHead>
                <TableHead>{t('accountingPages.writeOffPage.colVatRelief')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((r) => (
                <TableRow key={r.documentId} data-testid={`candidate-${r.number}`}>
                  <TableCell>
                    {isStaff ? (
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        aria-label={t('accountingPages.writeOffPage.selectInvoice', {
                          number: iso(r.number),
                        })}
                        checked={selected.has(r.documentId)}
                        onChange={() => setSelected(toggle(selected, r.documentId))}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/sales/invoices/$documentId"
                      params={{ documentId: r.documentId }}
                      className="text-primary hover:underline"
                    >
                      {r.number}
                    </Link>
                  </TableCell>
                  <TableCell>{r.contactName}</TableCell>
                  <TableCell>{formatDate(r.issueDate)}</TableCell>
                  <TableCell>{r.dueDate ? formatDate(r.dueDate) : ''}</TableCell>
                  <TableCell className="tnum text-end">{Math.max(0, r.daysOverdue)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(r.total)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(r.balance)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(r.vatShare)}</TableCell>
                  <TableCell>
                    <span title={r.relief.eligible ? undefined : r.relief.reasons.join('. ')}>
                      <Checkbox
                        label={t(
                          r.relief.eligible
                            ? 'accountingPages.writeOffPage.claim'
                            : 'accountingPages.writeOffPage.notEligible',
                        )}
                        disabled={!isStaff || !r.relief.eligible || !selected.has(r.documentId)}
                        checked={relief.has(r.documentId) && selected.has(r.documentId)}
                        onChange={() => setRelief(toggle(relief, r.documentId))}
                      />
                    </span>
                    {!r.relief.eligible && r.relief.reasons.length ? (
                      <span className="block text-xs text-muted-foreground">
                        {r.relief.reasons[0]}
                      </span>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {isStaff && totals.count ? (
        <div
          className="sticky bottom-0 mt-3 flex flex-wrap items-end gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm"
          data-testid="write-off-bar"
        >
          <Field label={t('accountingPages.writeOffPage.writeOffDate')} className="w-40">
            {(id) => <DateInput id={id} value={date} onChange={setDate} />}
          </Field>
          <Field label={t('accountingPages.writeOffPage.badDebtAccount')} className="w-72">
            {(id) => (
              <NativeSelect
                id={id}
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                {expenseAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} {a.fullName}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <div className="ms-auto text-end text-sm">
            <p>
              {t('accountingPages.writeOffPage.invoiceCount', { count: totals.count })} ·{' '}
              <span className="tnum font-semibold">
                {homeCurrency()} {formatMinor(totals.amount)}
              </span>
            </p>
            {totals.relief > 0n ? (
              <p className="tnum text-muted-foreground">
                {t('accountingPages.writeOffPage.reliefClaimed', {
                  amount: `${homeCurrency()} ${formatMinor(totals.relief)}`,
                })}
              </p>
            ) : null}
          </div>
          <Button
            onClick={() => {
              setNotified(false);
              setConfirming(true);
            }}
            disabled={!accountId || !date}
          >
            {t('accountingPages.writeOffPage.writeOff')}
          </Button>
        </div>
      ) : null}

      <ReasonDialog
        open={confirming}
        title={t('accountingPages.writeOffPage.confirmTitle', { count: totals.count })}
        description={
          totals.relief > 0n
            ? t('accountingPages.writeOffPage.confirmBodyRelief', {
                amount: `${homeCurrency()} ${formatMinor(totals.amount - totals.relief)}`,
                relief: `${homeCurrency()} ${formatMinor(totals.relief)}`,
              })
            : t('accountingPages.writeOffPage.confirmBody', {
                amount: `${homeCurrency()} ${formatMinor(totals.amount - totals.relief)}`,
              })
        }
        confirmLabel={t('accountingPages.writeOffPage.writeOff')}
        pending={write.isPending}
        error={
          write.error?.message ??
          (totals.relief > 0n && !notified
            ? t('accountingPages.writeOffPage.confirmNotified')
            : null)
        }
        onConfirm={(reason) => {
          if (totals.relief > 0n && !notified) return;
          write.mutate(reason);
        }}
        onClose={() => {
          write.reset();
          setConfirming(false);
        }}
      >
        {totals.relief > 0n ? (
          <Checkbox
            label={t('accountingPages.writeOffPage.notified')}
            checked={notified}
            onChange={(e) => setNotified(e.target.checked)}
          />
        ) : null}
      </ReasonDialog>
    </>
  );
}

function History() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const me = useMe();
  const isStaff = me.data?.isStaff ?? false;
  const t = useT();
  const lang = useLanguage();
  const iso = (v: string) => (lang === 'ar' ? ltr(v) : v);
  const list = useWriteOffs();
  const [undoing, setUndoing] = useState<WriteOff | null>(null);
  const undo = useMutation({
    mutationFn: ({ w, reason }: { w: WriteOff; reason: string }) =>
      api<WriteOff>(`/orgs/${orgId}/write-offs/${w.id}/undo`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async (w) => {
      await qc.invalidateQueries();
      setUndoing(null);
      toast(t('accountingPages.writeOffPage.toastUndone', { number: iso(w.number) }));
    },
  });
  if (list.isPending) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (list.isError)
    return (
      <EmptyState
        title={t('accountingPages.writeOffPage.historyLoadError')}
        hint={list.error.message}
      />
    );
  if (!list.data.length)
    return (
      <EmptyState
        title={t('accountingPages.writeOffPage.noneYet')}
        hint={t('accountingPages.writeOffPage.noneYetHint')}
      />
    );
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('accountingPages.writeOffInvoices.colDate')}</TableHead>
            <TableHead>{t('accountingPages.writeOffInvoices.colInvoice')}</TableHead>
            <TableHead>{t('accountingPages.writeOffPage.colCustomer')}</TableHead>
            <TableHead className="text-end">
              {t('accountingPages.writeOffInvoices.colAmount')}
            </TableHead>
            <TableHead className="text-end">
              {t('accountingPages.writeOffPage.colVatRelief')}
            </TableHead>
            <TableHead>{t('accountingPages.writeOffInvoices.colAccount')}</TableHead>
            <TableHead>{t('accountingPages.writeOffInvoices.colReason')}</TableHead>
            <TableHead>{t('accountingPages.writeOffInvoices.colStatus')}</TableHead>
            <TableHead className="w-24">
              {t('accountingPages.writeOffInvoices.colAction')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.data.map((w) => (
            <TableRow key={w.id}>
              <TableCell>{formatDate(w.date)}</TableCell>
              <TableCell>
                <Link
                  to="/sales/invoices/$documentId"
                  params={{ documentId: w.documentId }}
                  className="text-primary hover:underline"
                >
                  {w.number}
                </Link>
              </TableCell>
              <TableCell>{w.contactName}</TableCell>
              <TableCell className="tnum text-end">
                <Link
                  to="/accounting/journal-entries/$entryId"
                  params={{ entryId: w.journalEntryId }}
                  className="text-primary hover:underline"
                >
                  {formatAmount(w.amount)}
                </Link>
              </TableCell>
              <TableCell className="tnum text-end">
                {w.vatRelief !== '0.00' ? formatAmount(w.vatRelief) : ''}
                {w.vatReturnPeriod ? (
                  <span className="block text-xs text-muted-foreground">
                    {t('accountingPages.writeOffInvoices.returnPeriod', {
                      period: iso(w.vatReturnPeriod),
                    })}
                  </span>
                ) : null}
              </TableCell>
              <TableCell>{w.badDebtAccount}</TableCell>
              <TableCell className="max-w-[240px] truncate" title={w.reason}>
                {w.reason}
              </TableCell>
              <TableCell>
                {w.status === 'posted' ? (
                  <Badge variant="secondary">{t('accountingPages.writeOffInvoices.posted')}</Badge>
                ) : (
                  <span title={w.reverseReason ?? undefined}>
                    <Badge variant="outline">{t('accountingPages.writeOffInvoices.undone')}</Badge>
                  </span>
                )}
              </TableCell>
              <TableCell>
                {isStaff && w.status === 'posted' ? (
                  <Button variant="link" size="sm" className="px-1" onClick={() => setUndoing(w)}>
                    {t('accountingPages.writeOffInvoices.undo')}
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ReasonDialog
        open={undoing !== null}
        title={t('accountingPages.writeOffInvoices.undoTitle', {
          number: iso(undoing?.number ?? ''),
        })}
        description={t('accountingPages.writeOffInvoices.undoDescription')}
        confirmLabel={t('accountingPages.writeOffInvoices.undoConfirm')}
        destructive
        pending={undo.isPending}
        error={undo.error?.message ?? null}
        onConfirm={(reason) => undoing && undo.mutate({ w: undoing, reason })}
        onClose={() => {
          undo.reset();
          setUndoing(null);
        }}
      />
    </div>
  );
}
